import { appDelay, DillPaymentRequestResponse, formatMoney } from '@dill/dill-shared';
import React, { useMemo, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../../redux/store';
import { Download, ArrowClockwise, Link, CheckCircle, CircleDashed } from 'phosphor-react';
import { Edit3, Send, Trash } from 'react-feather';
import moment from 'moment';
import { COLORS } from '../../../../../utils/colors';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import {
  deletePaymentRequest,
  getPaymentRequests,
  resendPaymentRequest,
} from '../../../../../redux/services/paymentRequestsService';
import { openDillToast } from '../../../../../utils/helpers';
import { EditPaymentRequestModal } from '../EditPaymentRequestModal/EditPaymentRequestModal';
import {
  closeMessageModal,
  openMessageModal,
} from '../../../../../redux/globalSlices/genericSlice';
import { REACT_APP_FRONTEND_URL } from '../../../../../utils/constants';
import { ViewPaymentRequestModal } from '../ViewPaymentRequestModal/ViewPaymentRequestModal';
import { DillButton, DillTable } from '../../../../general/Dill';
import type { DillColumn, DillSortDir } from '../../../../general/Dill';

// DillTable styles header cells `text-[10px] uppercase tracking-wider` with `px-4` padding. Across
// ten columns that letter-spacing is what tips the longest labels onto a second line, so tighten
// tracking and hold each label on one line here rather than restyling the shared table.
const headerLabel = (text: string) => (
  <span className="whitespace-nowrap tracking-normal">{text}</span>
);

function PaymentRequestsTable({
  paymentRequests,
  paymentRequestIdToView,
  toolbarRight,
  toolbarLeft,
  hideCustomerColumn,
  height,
}: {
  paymentRequests: DillPaymentRequestResponse[];
  paymentRequestIdToView?: string;
  toolbarRight?: React.ReactNode;
  toolbarLeft?: React.ReactNode;
  hideCustomerColumn?: boolean;
  height?: string;
}) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [openEditPaymentModal, setOpenEditPaymentModal] = useState(false);
  const [openViewPaymentRequestModal, setViewPaymentRequestModal] = useState(false);
  const [selectedPaymentRequest, setSelectedPaymentRequest] =
    useState<DillPaymentRequestResponse | null>(null);
  const [sortKey, setSortKey] = useState('');
  const [sortDir, setSortDir] = useState<DillSortDir>('');

  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);

  useEffect(() => {
    if (paymentRequestIdToView && paymentRequests.length > 0 && !openViewPaymentRequestModal) {
      const paymentRequest = paymentRequests.find((pr) => pr.id === paymentRequestIdToView);
      if (paymentRequest) {
        setSelectedPaymentRequest(paymentRequest);
        setViewPaymentRequestModal(true);
      }
    }
  }, [paymentRequestIdToView, paymentRequests]);

  const handleResendPaymentRequest = async (data: DillPaymentRequestResponse) => {
    const paymentRequestId = data.id;
    const result = await dispatch(
      resendPaymentRequest({
        supplierId: currentSupplier?.id || '',
        paymentRequestId,
      })
    );
    if (result.type === 'paymentRequests/resendPaymentRequest/fulfilled') {
      openDillToast({ type: 'SUCCESS', message: 'Payment request resent successfully' });
      dispatch(getPaymentRequests({ supplierId: currentSupplier?.id || '' }));
    } else {
      openDillToast({ type: 'ERROR', message: 'Failed to resend payment request' });
    }
  };

  const handleCopyPaymentRequestLink = async (data: DillPaymentRequestResponse) => {
    const linkUrl = `${REACT_APP_FRONTEND_URL}/paymentRequestPayment/${data.id}`;
    await navigator.clipboard.writeText(linkUrl);
    openDillToast({ message: 'Link has successfuly been copied', type: 'SUCCESS' });
  };

  const handleEditPaymentRequest = (data: DillPaymentRequestResponse) => {
    setSelectedPaymentRequest(data);
    setOpenEditPaymentModal(true);
  };

  const handleDeletePaymentRequest = (data: DillPaymentRequestResponse) => {
    dispatch(
      openMessageModal({
        buttonText: 'Cancel',
        isSecondButton: true,
        secondButtonText: 'Delete',
        modalType: 'WARN',
        title: `Are you sure you want to delete this payment request?`,
        subTitle: 'This action can not be undone.',
        onSecondButtonClick: async () => {
          await appDelay(10);
          dispatch(
            openMessageModal({
              buttonText: '',
              modalType: 'LOADING',
              title: 'Deleting',
              subTitle: 'Deleting payment request',
            })
          );
          const deletePaymentRequestResults = await dispatch(
            deletePaymentRequest({
              paymentRequestId: data.id,
              supplierId: currentSupplier?.id || '',
            })
          );
          if (
            deletePaymentRequestResults.type === 'paymentRequests/deletePaymentRequest/rejected'
          ) {
            let errorMessage = '';
            if (
              deletePaymentRequestResults.type === 'paymentRequests/deletePaymentRequest/rejected'
            ) {
              errorMessage = deletePaymentRequestResults.payload as any;
            }
            dispatch(
              openMessageModal({
                buttonText: 'Close',
                modalType: 'ERROR',
                title: 'Error deleting payment request',
                subTitle: `${errorMessage} Contact (628) 304-0931 for customer support`,
              })
            );
          } else if (
            deletePaymentRequestResults.type === 'paymentRequests/deletePaymentRequest/fulfilled'
          ) {
            dispatch(
              openMessageModal({
                buttonText: 'Close',
                modalType: 'SUCCESS',
                title: 'Payment request deleted',
                subTitle: `Payment request deleted successfully`,
                onClose: () => {
                  dispatch(getPaymentRequests({ supplierId: currentSupplier?.id || '' }));
                },
              })
            );
          } else {
            closeMessageModal();
          }
        },
      })
    );
  };

  const handleSortChange = (key: string, dir: DillSortDir) => {
    setSortKey(key);
    setSortDir(dir);
  };

  const getCustomerName = (pr: DillPaymentRequestResponse) => {
    let name = pr?.buyer ? pr?.buyer.name.toLowerCase() : '';
    if (currentSupplier?.integrationType !== 'RUTTER_QBD' && pr?.buyer) {
      name = `${pr.buyer.name.toLowerCase()} - ${pr.buyer.quickbooksCustomerId}`;
    }
    return name;
  };

  const getStatus = (pr: DillPaymentRequestResponse) => {
    return pr.status === 'UNPAID' &&
      (pr.deliveryMethod === 'SMS' || pr.deliveryMethod === 'BOTH') &&
      (pr.smsStatus === 'queued' ||
        pr.smsStatus === 'sending' ||
        pr.smsStatus === 'sent' ||
        pr.smsStatus === 'accepted' ||
        pr.smsStatus === 'scheduled')
      ? 'PENDING'
      : pr.status;
  };

  // `width` is a *weight*, not a pixel size: DillTable is `resizable`, so it normalizes these to
  // fill the container proportionally. Leaving them unset gives every column an equal 120 share,
  // which is what squeezed the email column into its neighbour. The numbers below are written as
  // the px each column wants at a ~1545px-wide table, so they can be reasoned about against real
  // content — a wider container scales them all up, a narrower one down.
  const columns: DillColumn<DillPaymentRequestResponse>[] = useMemo(
    () => [
      {
        key: 'dateRequested',
        label: headerLabel('Requested'),
        sortable: true,
        width: 110,
        getSortValue: (row) => (row.createdAt ? new Date(row.createdAt).getTime() : 0),
      },
      {
        key: 'name',
        label: headerLabel('Customer'),
        sortable: true,
        width: 250,
        getSortValue: (row) => getCustomerName(row),
      },
      {
        key: 'emailAndPhoneNumber',
        label: headerLabel('Email/Phone Number'),
        sortable: false,
        width: 260,
      },
      {
        key: 'totalAmount',
        label: headerLabel('Total Amount'),
        sortable: true,
        numeric: true,
        width: 130,
        getSortValue: (row) => row.totalAmount || 0,
      },
      {
        key: 'balance',
        label: headerLabel('Balance'),
        sortable: true,
        numeric: true,
        width: 110,
        getSortValue: (row) => row.balance || 0,
      },
      {
        key: 'notes',
        label: headerLabel('Notes'),
        sortable: true,
        width: 200,
        getSortValue: (row) => row.notes || '',
      },
      {
        key: 'status',
        label: headerLabel('Status'),
        sortable: true,
        width: 150,
        getSortValue: (row) => row.status || '',
      },
      {
        key: 'attachments',
        label: headerLabel('Attachments'),
        sortable: false,
        width: 175,
      },
      {
        key: 'actions',
        label: headerLabel('Actions'),
        sortable: false,
        width: 160,
      },
    ],
    []
  );

  const columnRenderers: DillColumn<DillPaymentRequestResponse>[] = useMemo(
    () => [
      {
        key: 'dateRequested',
        label: 'Date Requested',
        render: (_, row) => (
          <p className="text-[11px] capitalize">
            {row.createdAt ? moment(row.createdAt).format('MM/DD/YYYY') : ''}
          </p>
        ),
      },
      {
        key: 'name',
        label: 'Customer',
        render: (_, row) => (
          <div className="flex flex-col gap-2 min-w-0">
            <p className="text-[11px] capitalize">{getCustomerName(row)}</p>
            {row.buyer?.branch && (
              // Labelled explicitly: branch values are often place names (and buyers carry their own
              // `city`), so an unlabelled pill under the customer name reads as the customer's city.
              // Neutral grey keeps it as metadata rather than competing with the status pill.
              <span
                title={`Branch: ${row.buyer.branch}`}
                className="self-start inline-flex max-w-full items-baseline gap-0.5 px-1 py-0 rounded bg-gray-100 text-[9px] font-medium uppercase tracking-wide break-words leading-normal">
                <span className="text-gray-400">Branch</span>
                <span className="text-gray-600">{row.buyer.branch}</span>
              </span>
            )}
          </div>
        ),
      },
      {
        key: 'emailAndPhoneNumber',
        label: <span className="flex text-xs text-nowrap">Email/Phone Number</span>,
        render: (_, row) => (
          // break-words so a long address wraps inside its cell instead of spilling into the
          // neighbouring column.
          <div className="min-w-0">
            {row?.emails && row?.emails.length > 0 && (
              <p className="text-[11px] ">{row.emails.join(' ')}</p>
            )}
            {row?.phoneNumber && <p className="text-[11px] break-words">{row.phoneNumber || ''}</p>}
          </div>
        ),
      },
      {
        key: 'totalAmount',
        label: 'Total Amount',
        numeric: true,
        render: (value) => (
          <p className="text-[11px] font-semibold text-gray-800">{formatMoney(`${value}`, 2)}</p>
        ),
      },
      {
        key: 'balance',
        label: 'Balance',
        numeric: true,
        render: (value) => (
          <p className="text-[11px] font-semibold text-gray-800">{formatMoney(`${value}`, 2)}</p>
        ),
      },
      {
        key: 'notes',
        label: 'Notes',
        render: (value) => <p className="text-[11px]">{`${value}`.slice(0, 50)}</p>,
      },
      {
        key: 'status',
        label: 'Status',
        render: (_, row) => {
          const status = getStatus(row);
          return (
            <div className="w-full flex flex-col max-w-[90px]">
              {status === 'PAID' && (
                <div className="flex SUCCESS_100-BG text-[11px] SUCCESS_500-CLR font-semibold px-2 py-1 rounded-full items-center justify-center">
                  <CheckCircle size={14} color={COLORS.SUCCESS_500} />
                  <div className="ml-2 text-[11px]">Paid</div>
                </div>
              )}
              {status === 'PENDING' && (
                <div className="flex flex-col gap-0.5">
                  <div className="flex WARNING_100-BG text-[10px] WARNING_500-CLR font-semibold px-1.5 py-0.5 rounded-full items-center justify-center">
                    <CircleDashed size={10} color={COLORS.WARNING_500} />
                    <div className="ml-1 text-[10px]">SMS sending</div>
                  </div>
                  {row.deliveryMethod === 'BOTH' && (
                    <div className="flex PRIMARY_100-BG text-[10px] PRIMARY_500-CLR font-semibold px-1.5 py-0.5 rounded-full items-center justify-center">
                      <Send size={10} color={COLORS.PRIMARY_500} />
                      <div className="ml-1 text-[10px]">Email sent</div>
                    </div>
                  )}
                </div>
              )}
              {status === 'UNPAID' && (
                <div className="flex PRIMARY_100-BG text-[11px] PRIMARY_500-CLR font-semibold px-2 py-1 rounded-full items-center justify-center">
                  <Send size={14} color={COLORS.PRIMARY_500} />
                  <div className="ml-2 text-[11px]">Sent</div>
                </div>
              )}
              {status === 'PAYMENT_INITIATED' && (
                <div className="flex INFO_100-BG text-[11px] INFO_500-CLR font-semibold px-2 py-1 rounded-full items-center justify-center">
                  <CircleDashed size={20} color={COLORS.INFO_500} />
                  <div className="ml-2 text-[11px]">Payment Initiated</div>
                </div>
              )}
              {status === 'PATIALLY_PAID' && (
                <div className="flex SUCCESS_100-BG text-[11px] SUCCESS_500-CLR font-semibold px-2 py-1 rounded-full items-center justify-center">
                  <CheckCircle size={14} color={COLORS.SUCCESS_500} />
                  <div className="ml-2 text-[11px]">Partially Paid</div>
                </div>
              )}
            </div>
          );
        },
      },
      {
        key: 'attachments',
        label: 'Attachments',
        render: (_, row) => {
          const attachments = row?.attachments ?? [];
          if (attachments.length === 0) return null;
          const [first, ...rest] = attachments;
          // Stay on one line. Rows are a fixed 60px and this column is narrow, so a chip per file
          // grew the cell past the row and spilled into the row below. Show the first file and fold
          // the rest into a counter — clicking the row opens the modal, which lists them all.
          return (
            <div className="flex w-full min-w-0 items-center gap-1 overflow-hidden">
              <a
                href={first?.url}
                target="_blank"
                rel="noreferrer"
                title={first?.name}
                // Downloading shouldn't also trigger the row's view modal.
                onClick={(e) => e.stopPropagation()}
                className="inline-flex min-w-0 items-center gap-1 text-[11px] text-blue-500 hover:text-blue-700">
                <span className="truncate">{first?.name || 'Attachment'}</span>
                <Download size={13} className="shrink-0" />
              </a>
              {rest.length > 0 && (
                <span
                  title={rest.map((attachment) => attachment.name).join(', ')}
                  className="shrink-0 px-1.5 py-0.5 rounded-full bg-gray-100 text-[10px] font-medium text-gray-500">
                  +{rest.length}
                </span>
              )}
            </div>
          );
        },
      },
      {
        key: 'actions',
        label: 'Actions',
        render: (_, row) => {
          const status = getStatus(row);
          const index = paymentRequests.findIndex((pr) => pr.id === row.id);
          return (
            <div className="flex items-center justify-center w-full gap-1">
              {(status === 'UNPAID' || status === 'PENDING') && (
                <div
                  data-tooltip-id={'copy_payment_request_link_' + index}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopyPaymentRequestLink(row);
                  }}>
                  <DillButton
                    variant="icon-default"
                    icon={<Link color={COLORS.GREY_500} size={16} />}
                    onClick={() => {}}
                  />
                </div>
              )}
              {(status === 'UNPAID' || status === 'PENDING') && (
                <div
                  data-tooltip-id={'resend_payment_request_' + index}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleResendPaymentRequest(row);
                  }}>
                  <DillButton
                    variant="icon-default"
                    icon={<ArrowClockwise color={COLORS.GREY_500} size={16} />}
                    onClick={() => {}}
                  />
                </div>
              )}
              {(status === 'UNPAID' || status === 'PENDING') && (
                <div
                  data-tooltip-id={'edit_payment_request_' + index}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditPaymentRequest(row);
                  }}>
                  <DillButton
                    variant="icon-default"
                    icon={<Edit3 color={COLORS.GREY_500} size={16} />}
                    onClick={() => {}}
                  />
                </div>
              )}
              {(status === 'UNPAID' || status === 'PENDING') && (
                <div
                  data-tooltip-id={'delete_payment_request_' + index}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeletePaymentRequest(row);
                  }}>
                  <DillButton variant="icon-red" icon={<Trash size={16} />} onClick={() => {}} />
                </div>
              )}
              <ReactTooltip
                id={'copy_payment_request_link_' + index}
                place={'top'}
                variant="dark"
                opacity={'100%'}
                style={{
                  background: '#222',
                  zIndex: 999999,
                }}>
                <span className="text-xs">copy request link</span>
              </ReactTooltip>
              <ReactTooltip
                id={'resend_payment_request_' + index}
                place={'top'}
                variant="dark"
                opacity={'100%'}
                style={{
                  background: '#222',
                  width: '84px',
                  zIndex: 999999,
                }}>
                <span className="text-xs">Resend</span>
              </ReactTooltip>
              <ReactTooltip
                id={'edit_payment_request_' + index}
                place={'top'}
                variant="dark"
                opacity={'100%'}
                style={{
                  background: '#222',
                  width: '64px',
                  zIndex: 999999,
                }}>
                <span className="text-xs">Edit</span>
              </ReactTooltip>
              <ReactTooltip
                id={'delete_payment_request_' + index}
                place={'top'}
                variant="dark"
                opacity={'100%'}
                style={{
                  background: '#222',
                  width: '78px',
                  zIndex: 999999,
                }}>
                <span className="text-xs">Delete</span>
              </ReactTooltip>
            </div>
          );
        },
      },
    ],
    [paymentRequests]
  );

  const finalColumns: DillColumn<DillPaymentRequestResponse>[] = columns
    .filter((col) => !(hideCustomerColumn && col.key === 'name'))
    .map((col) => {
      const renderer = columnRenderers.find((r) => r.key === col.key);
      return renderer ? { ...col, render: renderer.render } : col;
    });

  const sortedData = useMemo(() => {
    if (!sortKey || !sortDir) return paymentRequests;
    return [...paymentRequests].sort((a, b) => {
      let valueA: any;
      let valueB: any;

      const sortCol = columns.find((c) => c.key === sortKey);
      if (sortCol?.getSortValue) {
        valueA = sortCol.getSortValue(a);
        valueB = sortCol.getSortValue(b);
      } else {
        return 0;
      }

      if (typeof valueA === 'number' && typeof valueB === 'number') {
        return sortDir === 'asc' ? valueA - valueB : valueB - valueA;
      }

      const strA = String(valueA || '').toLowerCase();
      const strB = String(valueB || '').toLowerCase();
      if (strA < strB) return sortDir === 'asc' ? -1 : 1;
      if (strA > strB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [paymentRequests, sortKey, sortDir, columns]);

  const defaultSort = sortKey && sortDir ? { key: sortKey, dir: sortDir } : undefined;

  const handleRowClick = (row: DillPaymentRequestResponse) => {
    setSelectedPaymentRequest(row);
    setViewPaymentRequestModal(true);
  };

  return (
    <div className="w-full h-full flex flex-col relative pt-4">
      <EditPaymentRequestModal
        open={openEditPaymentModal}
        handleClose={() => setOpenEditPaymentModal(false)}
        selectedPaymentRequest={selectedPaymentRequest}
      />
      <ViewPaymentRequestModal
        open={openViewPaymentRequestModal}
        handleClose={() => {
          setViewPaymentRequestModal(false);
          const params = new URLSearchParams(location.search);
          if (params.has('paymentRequestId')) {
            params.delete('paymentRequestId');
            navigate(`${location.pathname}?${params.toString()}`, { replace: true });
          }
        }}
        selectedPaymentRequest={selectedPaymentRequest}
      />
      <DillTable<DillPaymentRequestResponse>
        columns={finalColumns}
        data={sortedData}
        rowKey={(row) => row.id}
        sortable
        sortCycle="3state"
        defaultSort={defaultSort}
        onRowClick={handleRowClick}
        striped={false}
        virtualize
        rowHeight={60}
        emptyMessage="No Payment Requests"
        toolbarRight={toolbarRight}
        toolbarLeft={toolbarLeft}
        showCount={false}
        resizable={true}
        searchClassName="h-9"
        maxHeight={height}
      />
    </div>
  );
}

export default PaymentRequestsTable;
